import { zodResolver } from '@hookform/resolvers/zod';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useAutoSave } from '@/hooks/use-auto-save';
import { allergens } from '@/lib/consts';
import { cn } from '@/lib/utils';
import { AllergenStatus, IngredientAllergen } from '@/types';

import { useInsertIngredientAllergen } from '../api/ingredients-mutations';
import { IngredientWithDetails } from '../types/ingredient-types';

const allergensFormSchema = z.object({
  allergens: z.array(z.number()),
  mayContain: z.array(z.number()),
});

type AllergensForm = z.infer<typeof allergensFormSchema>;

interface IngredientAllergensPresentationProps {
  ingredient: IngredientWithDetails;
  onSubmit: (values: AllergensForm) => Promise<void>;
  isEditable: boolean;
}

function IngredientAllergensPresentation({
  ingredient,
  onSubmit,
  isEditable,
}: IngredientAllergensPresentationProps) {
  const form = useForm<AllergensForm>({
    resolver: zodResolver(allergensFormSchema),
    defaultValues: {
      allergens: ingredient.latest_ingredient_allergen
        .filter((allergen) => allergen.status === 'present')
        .map((allergen) => allergen.allergen_id),
      mayContain: ingredient.latest_ingredient_allergen
        .filter((allergen) => allergen.status === 'may_be_present')
        .map((allergen) => allergen.allergen_id),
    },
  });

  const selectedAllergens = form
    .watch('allergens')
    .map((id) => allergens.find((allergen) => allergen.value === id))
    .filter((allergen) => allergen !== undefined);

  const selectedMayContains = form
    .watch('mayContain')
    .map((id) => allergens.find((allergen) => allergen.value === id))
    .filter((allergen) => allergen !== undefined);

  const filteredAllergens = allergens.filter(
    (allergen) => !form.watch('mayContain').includes(allergen.value),
  );

  const filteredMayContains = allergens.filter(
    (allergen) => !form.watch('allergens').includes(allergen.value),
  );

  useAutoSave(form, onSubmit);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {' '}
      </form>
      <div className="flex gap-4">
        <div className="flex-1">
          {/* Allergens */}
          <FormField
            control={form.control}
            name="allergens"
            render={({ field }) => (
              <FormItem className="flex flex-col flex-1">
                <div className="flex items-center justify-between">
                  <FormLabel className="whitespace-nowrap text-md">
                    Allergens:
                  </FormLabel>
                  <FormMessage></FormMessage>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        role="combobox"
                        disabled={!isEditable}
                        className={cn(
                          'w-full justify-between',
                          'text-muted-foreground',
                        )}
                      >
                        Change allergens
                        <ChevronsUpDown className="opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <CommandInput
                        placeholder="Search allergens..."
                        className="h-9"
                      />
                      <CommandList>
                        <CommandEmpty>No allergen found.</CommandEmpty>
                        <CommandGroup>
                          {filteredAllergens.map((allergen) => (
                            <CommandItem
                              value={allergen.label}
                              key={allergen.value}
                              onSelect={() => {
                                const newVal = field.value.includes(
                                  allergen.value,
                                )
                                  ? field.value.filter(
                                      (id) => id !== allergen.value,
                                    )
                                  : [...field.value, allergen.value];

                                form.setValue('allergens', newVal, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                });
                              }}
                            >
                              {allergen.label}
                              <Check
                                className={cn(
                                  'ml-auto',
                                  field.value.includes(allergen.value)
                                    ? 'opacity-100'
                                    : 'opacity-0',
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </FormItem>
            )}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {selectedAllergens.map((allergen) => (
              <span
                key={allergen.value}
                className="bg-secondary text-sm px-2.5 py-1 rounded"
              >
                {allergen.label}
              </span>
            ))}
          </div>
        </div>
        <div className="flex-1">
          {/* May Contains */}
          <FormField
            control={form.control}
            name="mayContain"
            render={({ field }) => (
              <FormItem className="flex flex-col flex-1">
                <div className="flex items-center justify-between">
                  <FormLabel className="whitespace-nowrap text-md">
                    May Contains:
                  </FormLabel>
                  <FormMessage></FormMessage>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        role="combobox"
                        disabled={!isEditable}
                        className={cn(
                          'w-full justify-between',
                          'text-muted-foreground',
                        )}
                      >
                        Change allergens
                        <ChevronsUpDown className="opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <CommandInput
                        placeholder="Search allergens..."
                        className="h-9"
                      />
                      <CommandList>
                        <CommandEmpty>No allergen found.</CommandEmpty>
                        <CommandGroup>
                          {filteredMayContains.map((allergen) => (
                            <CommandItem
                              value={allergen.label}
                              key={allergen.value}
                              onSelect={() => {
                                const newVal = field.value.includes(
                                  allergen.value,
                                )
                                  ? field.value.filter(
                                      (id) => id !== allergen.value,
                                    )
                                  : [...field.value, allergen.value];

                                form.setValue('mayContain', newVal, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                });
                              }}
                            >
                              {allergen.label}
                              <Check
                                className={cn(
                                  'ml-auto',
                                  field.value.includes(allergen.value)
                                    ? 'opacity-100'
                                    : 'opacity-0',
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </FormItem>
            )}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {selectedMayContains.map((allergen) => (
              <span
                key={allergen.value}
                className="bg-blue-200 text-sm px-2.5 py-1 rounded"
              >
                {allergen.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Form>
  );
}

interface IngredientAllergensProps {
  ingredient: IngredientWithDetails;
  isEditable: boolean;
}

export function IngredientAllergens({
  ingredient,
  isEditable,
}: IngredientAllergensProps) {
  const insertIngredientAllergen = useInsertIngredientAllergen();

  const handleSubmit = useCallback(
    async (values: AllergensForm) => {
      // We need to determine which allergens and may contains have been added or removed
      const desiredState: Record<number, AllergenStatus> = {};
      const currentState: Record<number, AllergenStatus> = {};
      allergens.forEach((allergen) => {
        currentState[allergen.value] = 'not_present';

        if (values.allergens.includes(allergen.value))
          desiredState[allergen.value] = 'present';
        else if (values.mayContain.includes(allergen.value))
          desiredState[allergen.value] = 'may_be_present';
        else desiredState[allergen.value] = 'not_present';
      });

      // Then compute the current state
      ingredient.latest_ingredient_allergen.forEach((allergen) => {
        currentState[allergen.allergen_id] = allergen.status;
      });

      // Then we get the current state
      const diff: Omit<IngredientAllergen, 'id' | 'created_at'>[] = [];
      allergens.forEach((allergen) => {
        if (desiredState[allergen.value] !== currentState[allergen.value]) {
          diff.push({
            ingredient_id: ingredient.id,
            allergen_id: allergen.value,
            status: desiredState[allergen.value],
            organisation_id: ingredient.organisation_id,
          });
        }
      });

      // Finally, we write all the changes
      await insertIngredientAllergen.mutateAsync(diff);
    },
    [insertIngredientAllergen, ingredient],
  );

  return (
    <IngredientAllergensPresentation
      ingredient={ingredient}
      onSubmit={handleSubmit}
      isEditable={isEditable}
    />
  );
}
